import React from 'react';

import { faMinus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type Path, type UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { ALLOWED_DAYS } from 'src/features/memberships/types';
import { allowedDaysOptions } from 'src/features/memberships/utils/constants';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { type CreateSubscriptionPlanFormSchemaValues } from 'src/features/subscriptions/api/useCreatePrepaidPlan';
import { DURATION, DURATION_MAP, LANGUAGE, LANGUAGE_MAP } from 'src/features/subscriptions/types';
import subscriptionMessages from 'src/features/subscriptions/utils/messages';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger,
} from 'src/registry/ui/MultiSelect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { generalPriceMessages, generalValidationMessages } from 'src/utils/messages';

type SubscriptionPlanFormProps = {
    form: UseFormReturn<CreateSubscriptionPlanFormSchemaValues>;
    onSubmit: (values: CreateSubscriptionPlanFormSchemaValues) => void;
};
enum END_TYPE {
    END_DAY = 'END_DAY',
    END_CYCLE = 'END_CYCLE',
}
const END_TYPE_MAP = {
    [END_TYPE.END_DAY]: { label: subscriptionMessages.endDay, value: true },
    [END_TYPE.END_CYCLE]: { label: subscriptionMessages.endCycle, value: false },
};

enum PRICE {
    DUE_AMOUNT_WITH_TAX = 'dueAmountWithTax',
    DUE_TAX_PERCENT = 'dueTaxPercent',
}

const formatPrice = (price: number, fractionDigits?: number) => (price / 100).toFixed(fractionDigits || 2);

export default function SubscriptionPlanForm({ onSubmit, form }: SubscriptionPlanFormProps) {
    const { formatMessage } = useIntl();

    const { dueAmountWithTax, dueTaxPercent } = form.getValues();

    const [displayedValues, setDisplayedValues] = React.useState<{
        [PRICE.DUE_AMOUNT_WITH_TAX]: string;
        [PRICE.DUE_TAX_PERCENT]: string;
    }>({
        [PRICE.DUE_TAX_PERCENT]: (dueTaxPercent * 100).toFixed(2),
        [PRICE.DUE_AMOUNT_WITH_TAX]: formatPrice(dueAmountWithTax),
    });

    const handleAllowedDaysChange = (value: ALLOWED_DAYS) => {
        const allowedDays = form.getValues('allowedWeekDays');
        if (allowedDays.includes(value)) {
            form.setValue(
                'allowedWeekDays',
                // @ts-expect-error Weird ts behaviour, ignore for later
                allowedDays.filter((day) => day !== value),
            );
        } else {
            form.setValue('allowedWeekDays', [...allowedDays, value], { shouldValidate: true });
        }
    };

    const PriceFormField = React.useCallback(
        ({
            isPercent,
            fieldName,
            label,
            disabled,
            value,
            fractionDigits,
            setDisplayedValue,
        }: {
            isPercent?: boolean;
            value: string;
            fieldName: Path<CreateSubscriptionPlanFormSchemaValues>;
            label: string;
            disabled?: boolean;
            fractionDigits?: number;
            setDisplayedValue?: (value: string) => void;
        }) => {
            return (
                <FormField
                    control={form.control}
                    name={fieldName}
                    render={() => (
                        <FormItem variant="dialog">
                            <FormLabel required={!disabled} variant="dialog">
                                {label}
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <Input
                                        value={value}
                                        disabled={disabled}
                                        placeholder={isPercent ? '0' : '0.00'}
                                        inputMode="decimal"
                                        type="text"
                                        pattern="^\d*(\.?\d{0,2})?$"
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const isValid = /^(|0(\.\d{0,2})?|([1-9]\d*)(\.\d{0,2})?)$/.test(value);
                                            if (!isValid || value > 999999999) return;

                                            const newPrice = Number(
                                                (isPercent ? Number(value) / 100 : Number(value) * 100).toFixed(
                                                    fractionDigits || 2,
                                                ),
                                            );
                                            if (isNaN(newPrice) || newPrice < 0) return;
                                            if (setDisplayedValue) setDisplayedValue(value);
                                            return form.setValue(fieldName, newPrice);
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            );
        },
        [form],
    );

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <FormField
                control={form.control}
                name="plan_name"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel required variant="dialog">
                            {formatMessage(subscriptionMessages.name)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    placeholder={formatMessage(generalValidationMessages.fieldRequired)}
                                    value={field.value}
                                    onChange={(e) => {
                                        field.onChange(e);
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <PriceFormField
                value={displayedValues[PRICE.DUE_AMOUNT_WITH_TAX]}
                fieldName={PRICE.DUE_AMOUNT_WITH_TAX}
                label={formatMessage(generalPriceMessages.priceWithVat)}
                setDisplayedValue={(value) =>
                    setDisplayedValues({ ...displayedValues, [PRICE.DUE_AMOUNT_WITH_TAX]: value })
                }
            />
            <PriceFormField
                isPercent
                disabled
                value={displayedValues[PRICE.DUE_TAX_PERCENT]}
                fieldName={PRICE.DUE_TAX_PERCENT}
                label={`${formatMessage(generalPriceMessages.vat)} %`}
                fractionDigits={0}
                setDisplayedValue={(value) =>
                    setDisplayedValues({ ...displayedValues, [PRICE.DUE_TAX_PERCENT]: value })
                }
            />

            <PriceFormField
                value={((dueAmountWithTax * dueTaxPercent) / 100).toFixed(2)}
                fieldName="dueTaxAmount"
                disabled
                label={formatMessage(generalPriceMessages.vatAmount)}
            />
            <PriceFormField
                value={((dueAmountWithTax - dueAmountWithTax * dueTaxPercent) / 100).toFixed(2)}
                fieldName="dueAmount"
                disabled
                label={formatMessage(generalPriceMessages.priceWithoutVat)}
            />

            <FormField
                control={form.control}
                name="duration.unit"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel required variant="dialog">
                            {formatMessage(subscriptionMessages.cycleType)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={formatMessage(generalValidationMessages.fieldRequiredSelect)}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.values(DURATION).map((option) => (
                                            <SelectItem key={option} value={option}>
                                                {formatMessage(DURATION_MAP[option].type)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="duration.value"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel required variant="dialog">
                            {formatMessage(subscriptionMessages.cycleCount)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="number"
                                    value={field.value}
                                    onChange={(e) => {
                                        const formattedInput = Number(e.target.value);
                                        if (isNaN(formattedInput) || formattedInput < 0) return;
                                        form.setValue('duration.value', formattedInput);
                                    }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="allowedHours.fromHour"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel required variant="dialog">
                            {formatMessage(membershipMessages.timeFrame)}
                        </FormLabel>
                        <div className="flex w-full gap-4">
                            <FormControlStack>
                                <FormControl>
                                    <Input type="time" className="w-full" step={1} {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                            <FontAwesomeIcon className="h-10 w-3 text-sm text-gray-400" icon={faMinus} />
                            <FormField
                                control={form.control}
                                name="allowedHours.toHour"
                                render={({ field }) => (
                                    <FormControlStack>
                                        <FormControl>
                                            <Input type="time" className="w-full" step={1} {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                )}
                            />
                        </div>
                    </FormItem>
                )}
            />

            <FormField
                control={form.control}
                name="allowedWeekDays"
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel required variant="dialog">
                            {formatMessage(subscriptionMessages.allowedDays)}
                        </FormLabel>
                        <FormControlStack>
                            <div className="flex w-full flex-wrap gap-2">
                                {allowedDaysOptions.map((day) => (
                                    <Button
                                        onClick={() => handleAllowedDaysChange(day.value)}
                                        key={`option-${day.value}`}
                                        className="flex-1 px-2"
                                        variant={
                                            form.getValues('allowedWeekDays').includes(day.value)
                                                ? 'default'
                                                : 'outline-solid'
                                        }
                                        type="button"
                                    >
                                        {formatMessage(day.label).substring(0, 3)}
                                    </Button>
                                ))}
                            </div>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="endAtMidnight"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel required variant="dialog">
                            {formatMessage(subscriptionMessages.endType)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select
                                    onValueChange={(value: END_TYPE) => {
                                        field.onChange(END_TYPE_MAP[value].value);
                                    }}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={formatMessage(generalValidationMessages.fieldRequiredSelect)}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.values(END_TYPE).map((option) => (
                                            <SelectItem key={option} value={option}>
                                                {formatMessage(END_TYPE_MAP[option].label)}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="languages"
                render={({ field }) => {
                    return (
                        <FormItem variant="dialog">
                            <FormLabel required variant="dialog">
                                <FormattedMessage {...subscriptionMessages.language} />
                            </FormLabel>
                            <FormControlStack>
                                <FormControl>
                                    <MultiSelect value={field.value} onChange={field.onChange}>
                                        <MultiSelectTrigger asChild>
                                            <MultiSelectControl>
                                                {field.value.reduce((previousValue, language, index) => {
                                                    if (!LANGUAGE_MAP[language as LANGUAGE]) return previousValue;
                                                    return `${previousValue}${index ? ', ' : ''}${formatMessage(LANGUAGE_MAP[language as LANGUAGE])}`;
                                                }, '') || (
                                                    <span className="text-gray-400">
                                                        {formatMessage(generalValidationMessages.fieldRequiredArray)}
                                                    </span>
                                                )}
                                            </MultiSelectControl>
                                        </MultiSelectTrigger>
                                        <MultiSelectContent>
                                            {Object.values(LANGUAGE).map((language) => (
                                                <MultiSelectOption key={language} selected={language}>
                                                    {formatMessage(LANGUAGE_MAP[language])}
                                                </MultiSelectOption>
                                            ))}
                                        </MultiSelectContent>
                                    </MultiSelect>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    );
                }}
            />

            <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(subscriptionMessages.description)}</FormLabel>
                        <FormControlStack>
                            <textarea
                                value={field.value}
                                onChange={field.onChange}
                                className="focus-visible:ring-primary-600 flex min-h-20 w-full rounded-md border border-gray-200 bg-white px-4 py-2 shadow-xs ring-offset-white transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-400 hover:border-gray-300 hover:shadow-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:border-gray-300 disabled:bg-gray-100 disabled:opacity-50 disabled:shadow-none dark:border-gray-800 dark:bg-gray-950 dark:ring-offset-gray-950 dark:placeholder:text-gray-400 dark:focus-visible:ring-gray-300"
                            />
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}
